import { getTraceData } from "@sentry/react";
import { withRequestFailureChecks } from "../request-failure-client";
// Shared bootstrap for every domain module under `./web/*`: one lazily-built
// Effect HttpClient, session auth injected per request from the shared token
// acquirer so this client and the axios client share one single-flight
// refresh. A rejected session is handled here for every endpoint: the session
// is cleared, the page navigates to login, and the call never settles.

import {
  FetchHttpClient,
  HttpClient,
  HttpClientError,
  HttpClientRequest,
} from "@effect/platform";
import { makeSessionClient, type SessionClient } from "@pima/web-api/client";
import { Effect } from "effect";
import { acquireAccessToken, clearSessionCookies } from "../axios-client";
import {
  withBoundaryResponseDecode,
  withBoundaryResponseStatus,
} from "../boundary";

// A refresh that gets no verdict fails the request as a transport error,
// before it is sent, exactly as it would fail offline.
const withSessionAuth = (
  client: HttpClient.HttpClient,
): HttpClient.HttpClient =>
  HttpClient.mapRequestEffect(client, (request) =>
    Effect.tryPromise({
      try: () => acquireAccessToken(),
      catch: (cause) =>
        new HttpClientError.RequestError({
          request,
          reason: "Transport",
          cause,
        }),
    }).pipe(
      Effect.map((token) =>
        token === null
          ? request
          : HttpClientRequest.setHeader(
              request,
              "Authorization",
              `Bearer ${token}`,
            ),
      ),
    ),
  );

const sessionClient = Effect.runSync(
  Effect.cached(
    makeSessionClient("/api/web", {
      transformClient: (client) =>
        withBoundaryResponseStatus(
          withRequestFailureChecks(
            HttpClient.mapRequest(withSessionAuth(client), (request) => {
              const trace = getTraceData()["sentry-trace"];
              return trace
                ? HttpClientRequest.setHeader(request, "sentry-trace", trace)
                : request;
            }),
          ),
        ),
      transformResponse: withBoundaryResponseDecode,
      onUnauthorized: Effect.sync(clearSessionCookies),
    }).pipe(Effect.provide(FetchHttpClient.layer)),
  ),
);

/** Shared lazy Effect for new API adapters, with one cached client across callers. */
export const getClientEffect = () => sessionClient;

/** Compatibility execution for unchanged Promise-based domain modules. */
export const getClient = (): Promise<SessionClient> =>
  Effect.runPromise(sessionClient);
